import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {FormattedMessage} from 'react-intl';
import messages from './messages';
import {coral, grey, supportColors} from 'components/primitives/color';
import {Div} from 'components/primitives/body';
import successIcon from './assets/success.svg';
import errorIcon from './assets/error.svg';
import infoIcon from './assets/info.svg';
import loadingIcon from './assets/loading.svg';
import {withManageToasts} from 'containers/Toasts/withManageToasts';

export class Toast extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            mouseOver: false
        };
    }

    componentDidMount() {
        if (this.props.closeAfter) {
            this.setCloseTimeout();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.closeAfter && this.props.closeAfter !== prevProps.closeAfter) {
            this.cancelCloseTimeout();
            this.setCloseTimeout();
        }
        else if (!this.props.closeAfter) {
            this.cancelCloseTimeout();
        }
    }

    componentWillUnmount() {
        this.cancelCloseTimeout();
    }

    setCloseTimeout = (closeAfter = this.props.closeAfter) => {
        this.closeTimeout = window.setTimeout(this.closeToast, closeAfter);
    };

    cancelCloseTimeout = () => {
        window.clearTimeout(this.closeTimeout);
    };

    closeToast = () => {
        this.props.removeToast(this.props.toastId);
    };

    getIcon = () => {
        const {loading, error, success} = this.props;
        if (loading) {
            return <LoadingBorder><LoadingIcon/></LoadingBorder>;
        }

        if (error) {
            return <ErrorBorder><ErrorIcon/></ErrorBorder>;
        }

        if (success) {

            return <SuccessBorder><SuccessIcon/></SuccessBorder>;

        }

        return <InfoBorder><InfoIcon/></InfoBorder>;
    };

    getInsetColor = () => {
        const {loading, error, success} = this.props;
        if (loading) {
            return coral.regular;
        }

        if (error) {
            return supportColors.crimson;
        }

        if (success) {
            return supportColors.teal;
        }

        return coral.regular;
    };

    mouseEnter = () => {
        this.setState({mouseOver: true});
    };

    mouseLeave = () => {
        this.setState({mouseOver: false});
    };

    renderCloseButton = () => {
        const visible = !this.props.loading && this.state.mouseOver;
        return (
            <I visible={visible} onClick={this.closeToast} className="fa material-icons">close</I>
        );
    };

    renderContent = () => {
        if (this.props.children) {
            return this.props.children;
        }

        // Different default contents.
        if (this.props.loading) {
            return <FormattedMessage {...messages.loading}/>;
        }
        if (this.props.error) {
            return <FormattedMessage {...messages.error}/>;
        }
        if (this.props.success) {
            return <FormattedMessage {...messages.success}/>;
        }

        return null;
    };

    render() {
        return (
            <Wrapper insetColor={this.getInsetColor()} onMouseEnter={this.mouseEnter} onMouseLeave={this.mouseLeave}>
                <IconWrapper>
                    {this.getIcon()}
                </IconWrapper>
                <Content>
                    <Div>
                        {this.renderContent()}
                    </Div>
                </Content>
                {this.props.hideCloseButton ? null : (
                    <CloseButtonWrapper>
                        {this.renderCloseButton()}
                    </CloseButtonWrapper>
                )}
            </Wrapper>
        );
    }
}

const Wrapper = styled.div`
    max-width: 720px;
    width: 100%;
    background-color: ${grey.grey800};
    min-height: 72px;
    box-shadow: inset 0px 2px 0px ${({insetColor}) => insetColor};
    border-radius: 2px;
    box-sizing: border-box;
    display: flex;
`;

const IconWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin-right: 16px;
    margin-left: 16px;
`;

const IconBorder = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    box-sizing: border-box;
`;

const SuccessBorder = styled(IconBorder)`
    border: 2px solid ${supportColors.teal};
    background-color: ${grey.grey900};
`;

const SuccessIcon = styled.div`
    height: 100%;
    width: 100%;
    mask-repeat: no-repeat;
    mask-position: center;
    mask-image: url(${successIcon});
    mask-size: 24px;
    background-color: ${supportColors.teal};
`;

const LoadingBorder = styled(IconBorder)`
    border: 2px solid ${grey.grey600};
    background-color: ${grey.grey900};
`;

const LoadingIcon = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
    
    &:after {
        width: 100%;
        height: 100%;
        position: absolute; 
        mask-repeat: no-repeat;
        mask-position: center;
        mask-image: url(${loadingIcon});
        content: '';
        mask-size: 24px;
        background-color: ${coral.regular};
        animation: spin 1.5s ease-in-out infinite;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% {  transform: rotate(359deg); }
    }
`;

const ErrorBorder = styled(IconBorder)`
    border: 2px solid ${supportColors.crimson};
    background-color: ${grey.grey900};
`;

const ErrorIcon = styled.div`
    width: 100%;
    height: 100%;
    background-color: ${supportColors.crimson};
    mask-repeat: no-repeat;
    mask-position: center;
    mask-image: url(${errorIcon});
    mask-size: 24px;
`;

const InfoBorder = styled(IconBorder)`
    border: 2px solid ${grey.grey600};
    background-color: ${grey.grey900};
`;

const InfoIcon = styled.div`
    width: 100%;
    height: 100%;
    background-color: ${coral.regular};
    mask-repeat: no-repeat;
    mask-position: center;
    mask-image: url(${infoIcon});
    mask-size: 24px;
`;

const Content = styled(Div)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    line-height: 1.2em;
    flex: 1;
`;

const CloseButtonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-left: 16px;
    margin-right: 16px;
`;

const I = styled.i`
  font-size: 20px;
  color: ${grey.grey400};
  cursor: pointer;
  visibility: ${({visible}) => visible ? 'initial' : 'hidden'};
`;


Toast.propTypes = {
    toastId: PropTypes.string,
    success: PropTypes.bool,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    closeAfter: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    removeToast: PropTypes.func,
    hideCloseButton: PropTypes.bool
};

export default withManageToasts(Toast);
